import { resolveNodeType } from './nodeConfig';
import {
  formatIncomingRate,
  formatInputUnit,
  isMassFlowInputUnit,
  looksLikeRateUnit,
  quantityUnitFromInputUnit,
} from './inputRateUnits';
import type { GenerationProfile } from '../types/nodeConfig';
import type { ProjectDocument, ProjectNode } from '../types/project';

// ------------------------------------------------------------
// Normalize input rate fields and project geometry on load.
// ------------------------------------------------------------

export function effectiveConstantInputRate(node: Record<string, unknown>): number {
  const ppm = Number(node.products_per_minute) || 0;
  const profile = node.generation_profile as GenerationProfile | undefined;
  if (profile?.type === 'constant') {
    const rate = profile.rate ?? 0;
    if (rate <= 0) return ppm;
    if (Math.abs(rate - 1) < 1e-6 && Math.abs(ppm - rate) > 0.01) return ppm;
    return rate;
  }
  return ppm;
}

export function effectiveInputRateLabel(node: Record<string, unknown>): string {
  const rate = effectiveConstantInputRate(node);
  const inputUnit = String(node.input_unit ?? 'kg/min');
  return formatIncomingRate(rate, inputUnit);
}

export function normalizeInputNode(node: ProjectNode): ProjectNode {
  if (resolveNodeType(node) !== 'input') return node;

  let next: ProjectNode = { ...node };
  const rawUnit = String(node.input_unit ?? '').trim();
  if (rawUnit && !looksLikeRateUnit(rawUnit)) {
    const existingLabel = String(node.product_unit_label ?? '').trim();
    const qty = quantityUnitFromInputUnit(rawUnit);
    next = {
      ...next,
      input_unit: formatInputUnit(qty),
      product_unit_label: existingLabel || rawUnit,
    };
  }

  const ppm = Number(next.products_per_minute) || 0;
  const profile = next.generation_profile as GenerationProfile | undefined;
  if (!profile || profile.type === 'constant') {
    const rate = profile?.type === 'constant' ? Number(profile.rate) || 0 : 1;
    if (ppm > 0 && (rate <= 0 || (Math.abs(rate - 1) < 1e-6 && Math.abs(ppm - rate) > 0.01))) {
      return {
        ...next,
        generation_profile: { type: 'constant', rate: ppm },
      };
    }
  }
  return next;
}

export function normalizeProjectDocument(document: ProjectDocument): ProjectDocument {
  return {
    ...document,
    nodes: document.nodes.map((node) => normalizeInputNode(node)),
  };
}

export function projectUsesMassFlow(document: ProjectDocument | null): boolean {
  if (!document) return false;
  return document.nodes.some((node) => {
    if (resolveNodeType(node) !== 'input') return false;
    return isMassFlowInputUnit(String(node.input_unit ?? ''));
  });
}

export function massUnitLabel(inputUnit: string): string {
  const qty = quantityUnitFromInputUnit(inputUnit);
  if (qty === 'ton' || qty === 'tons' || qty === 't') return 'tons';
  return qty;
}
